import React, { useState, useMemo } from 'react';
import { PointMensuel } from '../types';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

interface GraphiqueModalProps {
  type: 'marge' | 'sante';
  historique: PointMensuel[];
  onFermer: () => void;
}

type Periode = 'tout' | '5ans' | '1an' | 'annee' | '6mois' | '1mois';

const moisLabels: Record<string, string> = {
  '01': 'Jan', '02': 'Fév', '03': 'Mar', '04': 'Avr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Aoû',
  '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Déc',
};

const formatMois = (mois: string) => {
  const [annee, m] = mois.split('-');
  return `${moisLabels[m] || m} ${annee.slice(2)}`;
};

const formatEuro = (n: number) => `${Math.round(n / 1000).toLocaleString('fr-FR')}k €`;

const GraphiqueModal: React.FC<GraphiqueModalProps> = ({ type, historique, onFermer }) => {
  const [periode, setPeriode] = useState<Periode>('tout');

  const titre = type === 'marge'
    ? 'Évolution de la marge globale'
    : 'Évolution de la santé financière';

  const donneesFiltrees = useMemo(() => {
    if (periode === 'tout') return historique;

    const now = new Date();
    let cutoffDate: Date;

    switch (periode) {
      case '5ans':
        cutoffDate = new Date(now.getFullYear() - 5, now.getMonth(), 1);
        break;
      case '1an':
        cutoffDate = new Date(now.getFullYear() - 1, now.getMonth(), 1);
        break;
      case 'annee':
        cutoffDate = new Date(now.getFullYear(), 0, 1);
        break;
      case '6mois':
        cutoffDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
        break;
      case '1mois':
        cutoffDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        break;
      default:
        return historique;
    }

    const cutoffStr = `${cutoffDate.getFullYear()}-${String(cutoffDate.getMonth() + 1).padStart(2, '0')}`;
    return historique.filter(p => p.mois >= cutoffStr);
  }, [historique, periode]);

  const dataKey = type === 'marge' ? 'margeGlobale' : 'santeFinanciere';

  const strokeColor = '#7c3aed'; // violet-600
  const fillColor = '#7c3aed22';

  const periodes: { value: Periode; label: string }[] = [
    { value: 'tout', label: 'Tout' },
    { value: '5ans', label: '5 ans' },
    { value: '1an', label: '1 an' },
    { value: '6mois', label: '6 mois' },
    { value: '1mois', label: '1 mois' },
    { value: 'annee', label: 'Année' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{titre}</h3>
          <div className="flex items-center gap-4">
            {/* Sélecteur période */}
            <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
              {periodes.map(p => (
                <button
                  key={p.value}
                  onClick={() => setPeriode(p.value)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    periode === p.value
                      ? 'bg-violet-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <button
              onClick={onFermer}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Chart */}
        <div className="p-6">
          {donneesFiltrees.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <AreaChart data={donneesFiltrees} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="mois"
                  tickFormatter={formatMois}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis
                  tickFormatter={type === 'marge' ? formatEuro : (v: number) => `${v}`}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={false}
                  width={65}
                />
                {type === 'marge' && (
                  <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
                )}
                <Tooltip
                  formatter={(value: number) =>
                    type === 'marge'
                      ? [`${Math.round(value).toLocaleString('fr-FR')} €`, 'Marge globale']
                      : [`${value}/100`, 'Score santé']
                  }
                  labelFormatter={(label: string) => formatMois(label)}
                  contentStyle={{
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    fontSize: '12px',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey={dataKey}
                  stroke={strokeColor}
                  strokeWidth={2}
                  fill={fillColor}
                  dot={false}
                  activeDot={{ r: 5, fill: strokeColor }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
              Aucune donnée pour cette période
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GraphiqueModal;
